import { createTheme, alpha, PaletteMode, Shadows } from '@mui/material/styles';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module '@mui/material/styles/createPalette' {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange {}

  interface Palette {
    baseShadow: string;
  }
}

const defaultTheme = createTheme();

const customShadows: Shadows = [...defaultTheme.shadows];

export const brand = {
  50: 'hsl(210, 100%, 95%)',
  100: 'hsl(210, 100%, 92%)',
  200: 'hsl(210, 100%, 80%)',
  300: 'hsl(210, 100%, 65%)',
  400: 'hsl(210, 98%, 48%)',
  500: 'hsl(210, 98%, 42%)',
  600: 'hsl(210, 98%, 55%)',
  700: 'hsl(210, 100%, 35%)',
  800: 'hsl(210, 100%, 16%)',
  900: 'hsl(210, 100%, 21%)',
};

export const gray = {
  50: 'hsl(0, 0%, 100%)', // Blanc pur pour les cartes
  100: 'hsl(0, 0%, 98%)', // Fond très clair
  200: 'hsl(0, 0%, 96%)', // Bordures subtiles
  300: 'hsl(0, 0%, 92%)', // Bordures légères
  400: 'hsl(0, 0%, 75%)', // Texte secondaire clair
  500: 'hsl(0, 0%, 55%)', // Texte secondaire
  600: 'hsl(0, 0%, 40%)', // Texte secondaire foncé
  700: 'hsl(0, 0%, 30%)', // Texte principal
  800: 'hsl(0, 0%, 20%)', // Texte principal foncé
  900: 'hsl(0, 0%, 10%)', // Texte très foncé
};

export const green = {
  50: 'hsl(120, 80%, 98%)',
  100: 'hsl(120, 75%, 94%)',
  200: 'hsl(120, 75%, 87%)',
  300: 'hsl(120, 61%, 77%)',
  400: 'hsl(120, 44%, 53%)',
  500: 'hsl(120, 59%, 30%)',
  600: 'hsl(120, 70%, 25%)',
  700: 'hsl(120, 75%, 16%)',
  800: 'hsl(120, 84%, 10%)',
  900: 'hsl(120, 87%, 6%)',
};

export const orange = {
  50: 'hsl(45, 100%, 97%)',
  100: 'hsl(45, 92%, 90%)',
  200: 'hsl(45, 94%, 80%)',
  300: 'hsl(45, 90%, 65%)',
  400: 'hsl(45, 90%, 40%)',
  500: 'hsl(45, 90%, 35%)',
  600: 'hsl(45, 91%, 25%)',
  700: 'hsl(45, 94%, 20%)',
  800: 'hsl(45, 95%, 16%)',
  900: 'hsl(45, 93%, 12%)',
};

export const red = {
  50: 'hsl(0, 100%, 97%)',
  100: 'hsl(0, 92%, 90%)',
  200: 'hsl(0, 94%, 80%)',
  300: 'hsl(0, 90%, 65%)',
  400: 'hsl(0, 90%, 40%)',
  500: 'hsl(0, 90%, 30%)',
  600: 'hsl(0, 91%, 25%)',
  700: 'hsl(0, 94%, 18%)',
  800: 'hsl(0, 95%, 12%)',
  900: 'hsl(0, 93%, 6%)',
};

// Pastel color palettes - Rose/Pêche theme
export const pastelRose = {
  // Primary: Rose (teinte ~345°)
  50: 'hsl(345, 50%, 95%)',   // Très clair
  100: 'hsl(345, 45%, 92%)',
  200: 'hsl(345, 40%, 85%)',
  300: 'hsl(345, 35%, 75%)',
  400: 'hsl(345, 50%, 65%)',  // Main pour light mode
  500: 'hsl(345, 55%, 55%)',
  600: 'hsl(345, 50%, 45%)',
  700: 'hsl(345, 45%, 35%)',  // Main pour dark mode
  800: 'hsl(345, 40%, 25%)',
  900: 'hsl(345, 35%, 18%)',  // Très foncé
};

export const pastelMint = {
  // Success: Rose-vert/Menthe (teinte ~155°)
  50: 'hsl(155, 50%, 95%)',
  100: 'hsl(155, 45%, 92%)',
  200: 'hsl(155, 40%, 85%)',
  300: 'hsl(155, 35%, 75%)',
  400: 'hsl(155, 50%, 65%)',
  500: 'hsl(155, 55%, 55%)',
  600: 'hsl(155, 50%, 45%)',
  700: 'hsl(155, 45%, 35%)',
  800: 'hsl(155, 40%, 25%)',
  900: 'hsl(155, 35%, 18%)',
};

export const pastelPeach = {
  // Warning: Rose-orange/Pêche (teinte ~35°)
  50: 'hsl(35, 50%, 95%)',
  100: 'hsl(35, 45%, 92%)',
  200: 'hsl(35, 40%, 85%)',
  300: 'hsl(35, 35%, 75%)',
  400: 'hsl(35, 50%, 65%)',
  500: 'hsl(35, 55%, 55%)',
  600: 'hsl(35, 50%, 45%)',
  700: 'hsl(35, 45%, 35%)',
  800: 'hsl(35, 40%, 25%)',
  900: 'hsl(35, 35%, 18%)',
};

export const pastelCoral = {
  // Error: Rose-rouge/Corail (teinte ~5°)
  50: 'hsl(5, 50%, 95%)',
  100: 'hsl(5, 45%, 92%)',
  200: 'hsl(5, 40%, 85%)',
  300: 'hsl(5, 35%, 75%)',
  400: 'hsl(5, 50%, 65%)',
  500: 'hsl(5, 55%, 55%)',
  600: 'hsl(5, 50%, 45%)',
  700: 'hsl(5, 45%, 35%)',
  800: 'hsl(5, 40%, 25%)',
  900: 'hsl(5, 35%, 18%)',
};

export const pastelGray = {
  // Grey: Gris neutre avec légère teinte pastel (saturation réduite)
  50: 'hsl(0, 5%, 98%)',
  100: 'hsl(0, 5%, 96%)',
  200: 'hsl(0, 5%, 92%)',
  300: 'hsl(0, 5%, 85%)',
  400: 'hsl(0, 5%, 70%)',
  500: 'hsl(0, 5%, 55%)',
  600: 'hsl(0, 5%, 40%)',
  700: 'hsl(0, 5%, 30%)',
  800: 'hsl(0, 5%, 20%)',
  900: 'hsl(0, 5%, 12%)',
};

// Palette Earth - Basée uniquement sur les 4 couleurs fournies
// #F9F8F6, #EFE9E3, #D9CFC7, #C9B59C
export const earth = {
  // Palette basée sur les 4 couleurs fournies
  50: '#F9F8F6',   // rgb(249, 248, 246) - très clair, presque blanc
  100: '#EFE9E3',  // rgb(239, 233, 227) - beige clair
  200: '#D9CFC7',  // rgb(217, 207, 199) - taupe/greige clair
  300: '#C9B59C',  // rgb(201, 181, 156) - brun clair
  // Variations générées pour compléter la palette
  400: '#B8A088',  // Variation plus foncée
  500: '#A68B70',  // Couleur principale
  600: '#8F7558',  // Variation foncée
  700: '#6B5843',  // Très foncé
  800: '#4A3D2F',  // Presque noir
  900: '#2A2119',  // Noir avec teinte terre
};

// Palette Sage - Basée uniquement sur les 4 couleurs fournies
// #F8F3D9, #EBE5C2, #B9B28A, #504B38
export const sage = {
  // Palette basée sur les 4 couleurs fournies
  50: '#F8F3D9',   // rgb(248, 243, 217) - crème très clair
  100: '#EBE5C2',  // rgb(235, 229, 194) - beige clair
  200: '#B9B28A',  // rgb(185, 178, 138) - olive vert moyen
  300: '#504B38',  // rgb(80, 75, 56) - olive vert foncé
  // Variations générées pour compléter la palette
  400: '#8B8565',  // Variation intermédiaire
  500: '#7A7555',  // Couleur principale
  600: '#6B6648',  // Variation foncée
  700: '#5A553D',  // Très foncé
  800: '#454032',  // Presque noir
  900: '#2F2B20',  // Noir avec teinte olive
};

// Palette Ocean - Basée uniquement sur les 4 couleurs fournies
// #6DC3BB, #393D7E, #5459AC, #F2AEBB
export const ocean = {
  // Palette basée sur les 4 couleurs fournies
  50: '#F2AEBB',   // rgb(242, 174, 187) - rose clair
  100: '#6DC3BB',  // rgb(109, 195, 187) - teal/mint green clair
  200: '#5459AC',  // rgb(84, 89, 172) - periwinkle/slate blue moyen
  300: '#393D7E',  // rgb(57, 61, 126) - navy blue foncé
  // Variations générées pour compléter la palette
  400: '#4A4F9A',  // Variation intermédiaire
  500: '#3F4488',  // Couleur principale
  600: '#353A76',  // Variation foncée
  700: '#2A2E64',  // Très foncé
  800: '#1F2252',  // Presque noir
  900: '#141640',  // Noir avec teinte navy
};

// Palette White - Thème blanc minimaliste dans les règles de l'art
// Nuances de gris très subtiles pour la profondeur
export const white = {
  50: '#FFFFFF',   // Blanc pur
  100: '#FAFAFA',  // Blanc cassé très léger
  200: '#F5F5F5',  // Gris très clair
  300: '#EEEEEE',  // Gris clair pour bordures
  400: '#E0E0E0',  // Gris moyen clair
  500: '#BDBDBD',  // Gris moyen
  600: '#9E9E9E',  // Gris moyen foncé
  700: '#757575',  // Gris foncé
  800: '#424242',  // Gris très foncé
  900: '#212121',  // Presque noir pour texte
};

// Palette Black - Thème noir minimaliste dans les règles de l'art
// Nuances de gris très subtiles pour la profondeur
export const black = {
  50: '#FAFAFA',   // Blanc cassé pour contraste
  100: '#F5F5F5',  // Gris très clair
  200: '#EEEEEE',  // Gris clair
  300: '#E0E0E0',  // Gris moyen clair
  400: '#BDBDBD',  // Gris moyen
  500: '#9E9E9E',  // Gris moyen foncé
  600: '#757575',  // Gris foncé
  700: '#616161',  // Gris très foncé
  800: '#424242',  // Presque noir
  900: '#000000',  // Noir pur
};

// Premium Dark - Thème sombre professionnel avec accents orange/pêche
export const premium = {
  50: '#FFF5ED',   // Très léger orange pêche (pour backgrounds clairs)
  100: '#FFEBD4',  // Orange pêche clair
  200: '#FFD4A8',  // Orange pêche moyen clair
  300: '#FFB97C',  // Orange pêche moyen
  400: '#FFA35A',  // Orange pêche vif
  500: '#F5A962',  // Orange pêche principal (couleur accent de l'image)
  600: '#E08940',  // Orange foncé
  700: '#C0692C',  // Orange très foncé
  800: '#9D4F1C',  // Brun orangé
  900: '#7A3910',  // Brun foncé
};

// Gris pour le thème premium dark
export const premiumGray = {
  50: '#F8F9FA',   // Blanc cassé
  100: '#E9ECEF',  // Gris très clair
  200: '#9CA3AF',  // Gris moyen (texte secondaire de l'image)
  300: '#6B7280',  // Gris moyen foncé
  400: '#4B5563',  // Gris foncé
  500: '#374151',  // Gris très foncé
  600: '#2D333D',  // Presque noir
  700: '#27282C',  // Background des cartes (de l'image)
  800: '#1F2023',  // Entre background card et main
  900: '#1A1B1E',  // Background principal (de l'image)
};

export const getDesignTokens = (mode: PaletteMode) => {
  customShadows[1] =
    mode === 'dark'
      ? 'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px'
      : '0px 1px 3px 0px rgba(0, 0, 0, 0.05), 0px 1px 2px 0px rgba(0, 0, 0, 0.1)'; // Ombres très légères style BizLink

  return {
    palette: {
      mode,
      primary: {
        light: brand[200],
        main: brand[400],
        dark: brand[700],
        contrastText: brand[50],
        ...(mode === 'dark' && {
          contrastText: brand[50],
          light: brand[300],
          main: brand[400],
          dark: brand[700],
        }),
      },
      info: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
        ...(mode === 'dark' && {
          contrastText: brand[300],
          light: brand[500],
          main: brand[700],
          dark: brand[900],
        }),
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
        ...(mode === 'dark' && {
          light: orange[400],
          main: orange[500],
          dark: orange[700],
        }),
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
        ...(mode === 'dark' && {
          light: red[400],
          main: red[500],
          dark: red[700],
        }),
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
        ...(mode === 'dark' && {
          light: green[400],
          main: green[500],
          dark: green[700],
        }),
      },
      grey: {
        ...gray,
      },
      divider: mode === 'dark' ? alpha(gray[700], 0.6) : alpha(gray[200], 0.5),
      background: {
        default: 'hsl(0, 0%, 98%)', // Fond très clair, presque blanc
        paper: 'hsl(0, 0%, 100%)', // Cartes blanches
        ...(mode === 'dark' && { default: gray[900], paper: 'hsl(220, 30%, 7%)' }),
      },
      text: {
        primary: 'hsl(0, 0%, 20%)', // Texte principal sombre mais doux
        secondary: 'hsl(0, 0%, 50%)', // Texte secondaire gris moyen
        warning: orange[400],
        ...(mode === 'dark' && { primary: 'hsl(0, 0%, 100%)', secondary: gray[400] }),
      },
      action: {
        hover: alpha(gray[900], 0.04), // Hover très subtil
        selected: alpha(gray[900], 0.08), // Sélection légère
        ...(mode === 'dark' && {
          hover: alpha(gray[600], 0.2),
          selected: alpha(gray[600], 0.3),
        }),
      },
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
      h1: {
        fontSize: defaultTheme.typography.pxToRem(48),
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: -0.5,
      },
      h2: {
        fontSize: defaultTheme.typography.pxToRem(36),
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h3: {
        fontSize: defaultTheme.typography.pxToRem(30),
        lineHeight: 1.2,
      },
      h4: {
        fontSize: defaultTheme.typography.pxToRem(24),
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h5: {
        fontSize: defaultTheme.typography.pxToRem(20),
        fontWeight: 600,
      },
      h6: {
        fontSize: defaultTheme.typography.pxToRem(18),
        fontWeight: 600,
      },
      subtitle1: {
        fontSize: defaultTheme.typography.pxToRem(18),
      },
      subtitle2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 500,
      },
      body1: {
        fontSize: defaultTheme.typography.pxToRem(14),
      },
      body2: {
        fontSize: defaultTheme.typography.pxToRem(14),
        fontWeight: 400,
      },
      caption: {
        fontSize: defaultTheme.typography.pxToRem(12),
        fontWeight: 400,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: customShadows,
  };
};

export const colorSchemes = {
  light: {
    palette: {
      primary: {
        light: brand[200],
        main: brand[400],
        dark: brand[700],
        contrastText: brand[50],
      },
      info: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[200], 0.5),
      background: {
        default: 'hsl(0, 0%, 98%)', // Fond très clair, presque blanc
        paper: 'hsl(0, 0%, 100%)', // Cartes blanches
      },
      text: {
        primary: 'hsl(0, 0%, 20%)', // Texte principal sombre mais doux
        secondary: 'hsl(0, 0%, 50%)', // Texte secondaire gris moyen
        warning: orange[400],
      },
      action: {
        hover: alpha(gray[900], 0.04), // Hover très subtil
        selected: alpha(gray[900], 0.08), // Sélection légère
      },
      baseShadow:
        '0px 1px 3px 0px rgba(0, 0, 0, 0.05), 0px 1px 2px 0px rgba(0, 0, 0, 0.1)', // Ombres très légères style BizLink
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: brand[50],
        light: brand[300],
        main: brand[400],
        dark: brand[700],
      },
      info: {
        contrastText: brand[300],
        light: brand[500],
        main: brand[700],
        dark: brand[900],
      },
      warning: {
        light: orange[400],
        main: orange[500],
        dark: orange[700],
      },
      error: {
        light: red[400],
        main: red[500],
        dark: red[700],
      },
      success: {
        light: green[400],
        main: green[500],
        dark: green[700],
      },
      grey: {
        ...gray,
      },
      divider: alpha(gray[700], 0.6),
      background: {
        default: gray[900],
        paper: 'hsl(220, 30%, 7%)',
      },
      text: {
        primary: 'hsl(0, 0%, 100%)',
        secondary: gray[400],
      },
      action: {
        hover: alpha(gray[600], 0.2),
        selected: alpha(gray[600], 0.3),
      },
      baseShadow:
        'hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px',
    },
  },
};

export const colorSchemesPastel = {
  light: {
    palette: {
      primary: {
        light: pastelRose[200],
        main: pastelRose[400],
        dark: pastelRose[700],
        contrastText: pastelGray[900],
      },
      info: {
        light: pastelRose[100],
        main: pastelRose[300],
        dark: pastelRose[600],
        contrastText: pastelGray[50],
      },
      warning: {
        light: pastelPeach[300],
        main: pastelPeach[400],
        dark: pastelPeach[800],
      },
      error: {
        light: pastelCoral[300],
        main: pastelCoral[400],
        dark: pastelCoral[800],
      },
      success: {
        light: pastelMint[300],
        main: pastelMint[400],
        dark: pastelMint[800],
      },
      grey: {
        ...pastelGray,
      },
      divider: alpha(pastelGray[200], 0.5),
      background: {
        default: 'hsl(345, 20%, 98%)', // Fond très clair avec légère teinte rose
        paper: 'hsl(345, 15%, 100%)', // Cartes blanches avec très légère teinte
      },
      text: {
        primary: 'hsl(345, 10%, 20%)', // Texte principal avec légère teinte pastel
        secondary: 'hsl(345, 8%, 50%)', // Texte secondaire
        warning: pastelPeach[400],
      },
      action: {
        hover: alpha(pastelRose[400], 0.08), // Hover pastel subtil
        selected: alpha(pastelRose[400], 0.12), // Sélection pastel
      },
      baseShadow:
        '0px 1px 3px 0px rgba(345, 30%, 0%, 0.05), 0px 1px 2px 0px rgba(345, 25%, 0%, 0.1)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: pastelRose[50],
        light: pastelRose[300],
        main: pastelRose[700],
        dark: pastelRose[900],
      },
      info: {
        contrastText: pastelRose[300],
        light: pastelRose[500],
        main: pastelRose[700],
        dark: pastelRose[900],
      },
      warning: {
        light: pastelPeach[400],
        main: pastelPeach[500],
        dark: pastelPeach[700],
      },
      error: {
        light: pastelCoral[400],
        main: pastelCoral[500],
        dark: pastelCoral[700],
      },
      success: {
        light: pastelMint[400],
        main: pastelMint[500],
        dark: pastelMint[700],
      },
      grey: {
        ...pastelGray,
      },
      divider: alpha(pastelGray[700], 0.6),
      background: {
        default: 'hsl(345, 25%, 12%)', // Fond sombre avec teinte rose pastel
        paper: 'hsl(345, 20%, 15%)', // Cartes avec teinte pastel
      },
      text: {
        primary: 'hsl(345, 10%, 95%)', // Texte clair avec légère teinte
        secondary: pastelGray[400],
      },
      action: {
        hover: alpha(pastelRose[400], 0.2),
        selected: alpha(pastelRose[400], 0.3),
      },
      baseShadow:
        'hsla(345, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(345, 25%, 10%, 0.8) 0px 8px 16px -5px',
    },
  },
};

// ColorScheme Earth - Utilise uniquement la palette earth
export const colorSchemesEarth = {
  light: {
    palette: {
      primary: {
        light: earth[200],
        main: earth[300],
        dark: earth[600],
        contrastText: earth[900],
      },
      secondary: {
        light: earth[100],
        main: earth[200],
        dark: earth[500],
        contrastText: earth[900],
      },
      info: {
        light: earth[100],
        main: earth[200],
        dark: earth[500],
        contrastText: earth[900],
      },
      warning: {
        light: earth[200],
        main: earth[400],
        dark: earth[600],
        contrastText: earth[900],
      },
      error: {
        light: earth[200],
        main: earth[500],
        dark: earth[700],
        contrastText: earth[50],
      },
      success: {
        light: earth[100],
        main: earth[300],
        dark: earth[600],
        contrastText: earth[900],
      },
      grey: {
        ...earth,
      },
      divider: alpha(earth[200], 0.5),
      background: {
        default: earth[50],  // #F9F8F6 - très clair
        paper: earth[50],     // #F9F8F6 - très clair
      },
      text: {
        primary: earth[900],   // Texte très foncé
        secondary: earth[600], // Texte moyen
      },
      action: {
        hover: alpha(earth[300], 0.08),
        selected: alpha(earth[300], 0.12),
      },
      baseShadow:
        '0px 1px 3px 0px rgba(201, 181, 156, 0.1), 0px 1px 2px 0px rgba(201, 181, 156, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: earth[50],
        light: earth[200],
        main: earth[300],
        dark: earth[500],
      },
      secondary: {
        contrastText: earth[50],
        light: earth[100],
        main: earth[200],
        dark: earth[400],
      },
      info: {
        contrastText: earth[50],
        light: earth[100],
        main: earth[200],
        dark: earth[400],
      },
      warning: {
        contrastText: earth[50],
        light: earth[200],
        main: earth[400],
        dark: earth[600],
      },
      error: {
        contrastText: earth[50],
        light: earth[400],
        main: earth[500],
        dark: earth[700],
      },
      success: {
        contrastText: earth[50],
        light: earth[200],
        main: earth[300],
        dark: earth[500],
      },
      grey: {
        ...earth,
      },
      divider: alpha(earth[600], 0.6),
      background: {
        default: earth[900],  // Fond très foncé
        paper: earth[800],    // Cartes foncées
      },
      text: {
        primary: earth[50],   // Texte clair
        secondary: earth[200], // Texte secondaire clair
      },
      action: {
        hover: alpha(earth[300], 0.15),
        selected: alpha(earth[300], 0.25),
      },
      baseShadow:
        '0px 4px 16px 0px rgba(42, 33, 25, 0.7), 0px 8px 16px -5px rgba(42, 33, 25, 0.8)',
    },
  },
};

// ColorScheme Sage - Utilise uniquement la palette sage
export const colorSchemesSage = {
  light: {
    palette: {
      primary: {
        light: sage[100],
        main: sage[200],
        dark: sage[300],
        contrastText: sage[300],
      },
      secondary: {
        light: sage[50],
        main: sage[100],
        dark: sage[200],
        contrastText: sage[300],
      },
      info: {
        light: sage[50],
        main: sage[100],
        dark: sage[200],
        contrastText: sage[300],
      },
      warning: {
        light: sage[100],
        main: sage[200],
        dark: sage[300],
        contrastText: sage[300],
      },
      error: {
        light: sage[100],
        main: sage[200],
        dark: sage[300],
        contrastText: sage[50],
      },
      success: {
        light: sage[50],
        main: sage[200],
        dark: sage[300],
        contrastText: sage[300],
      },
      grey: {
        ...sage,
      },
      divider: alpha(sage[200], 0.5),
      background: {
        default: sage[50],  // #F8F3D9 - crème très clair
        paper: sage[50],     // #F8F3D9 - crème très clair
      },
      text: {
        primary: sage[300],   // #504B38 - olive vert foncé
        secondary: sage[200], // #B9B28A - olive vert moyen
      },
      action: {
        hover: alpha(sage[200], 0.12),
        selected: alpha(sage[200], 0.18),
      },
      baseShadow:
        '0px 1px 3px 0px rgba(80, 75, 56, 0.1), 0px 1px 2px 0px rgba(80, 75, 56, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: sage[50],
        light: sage[100],
        main: sage[200],
        dark: sage[300],
      },
      secondary: {
        contrastText: sage[50],
        light: sage[50],
        main: sage[100],
        dark: sage[200],
      },
      info: {
        contrastText: sage[50],
        light: sage[50],
        main: sage[100],
        dark: sage[200],
      },
      warning: {
        contrastText: sage[50],
        light: sage[100],
        main: sage[200],
        dark: sage[300],
      },
      error: {
        contrastText: sage[50],
        light: sage[100],
        main: sage[200],
        dark: sage[300],
      },
      success: {
        contrastText: sage[50],
        light: sage[50],
        main: sage[200],
        dark: sage[300],
      },
      grey: {
        ...sage,
      },
      divider: alpha(sage[200], 0.6),
      background: {
        default: sage[300],  // #504B38 - olive vert foncé
        paper: sage[800],     // Cartes foncées
      },
      text: {
        primary: sage[50],    // #F8F3D9 - crème très clair
        secondary: sage[100],  // #EBE5C2 - beige clair
      },
      action: {
        hover: alpha(sage[200], 0.2),
        selected: alpha(sage[200], 0.3),
      },
      baseShadow:
        '0px 4px 16px 0px rgba(47, 43, 32, 0.7), 0px 8px 16px -5px rgba(47, 43, 32, 0.8)',
    },
  },
};

// ColorScheme Ocean - Utilise uniquement la palette ocean
export const colorSchemesOcean = {
  light: {
    palette: {
      primary: {
        light: ocean[100],
        main: ocean[200],
        dark: ocean[300],
        contrastText: ocean[50],
      },
      secondary: {
        light: ocean[50],
        main: ocean[100],
        dark: ocean[200],
        contrastText: ocean[300],
      },
      info: {
        light: ocean[100],
        main: ocean[200],
        dark: ocean[300],
        contrastText: ocean[50],
      },
      warning: {
        light: ocean[50],
        main: ocean[100],
        dark: ocean[200],
        contrastText: ocean[300],
      },
      error: {
        light: ocean[50],
        main: ocean[100],
        dark: ocean[200],
        contrastText: ocean[300],
      },
      success: {
        light: ocean[100],
        main: ocean[200],
        dark: ocean[300],
        contrastText: ocean[50],
      },
      grey: {
        ...ocean,
      },
      divider: alpha(ocean[200], 0.5),
      background: {
        default: ocean[50],  // #F2AEBB - rose clair (utilisé comme fond clair)
        paper: '#FFFFFF',      // Blanc pur pour contraste
      },
      text: {
        primary: ocean[300],   // #393D7E - navy blue foncé
        secondary: ocean[200], // #5459AC - periwinkle blue moyen
      },
      action: {
        hover: alpha(ocean[200], 0.12),
        selected: alpha(ocean[200], 0.18),
      },
      baseShadow:
        '0px 1px 3px 0px rgba(57, 61, 126, 0.15), 0px 1px 2px 0px rgba(57, 61, 126, 0.1)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: ocean[50],
        light: ocean[100],
        main: ocean[200],
        dark: ocean[300],
      },
      secondary: {
        contrastText: ocean[50],
        light: ocean[50],
        main: ocean[100],
        dark: ocean[200],
      },
      info: {
        contrastText: ocean[50],
        light: ocean[100],
        main: ocean[200],
        dark: ocean[300],
      },
      warning: {
        contrastText: ocean[50],
        light: ocean[50],
        main: ocean[100],
        dark: ocean[200],
      },
      error: {
        contrastText: ocean[50],
        light: ocean[50],
        main: ocean[100],
        dark: ocean[200],
      },
      success: {
        contrastText: ocean[50],
        light: ocean[100],
        main: ocean[200],
        dark: ocean[300],
      },
      grey: {
        ...ocean,
      },
      divider: alpha(ocean[200], 0.6),
      background: {
        default: ocean[300],  // #393D7E - navy blue foncé
        paper: ocean[800],    // Cartes foncées
      },
      text: {
        primary: ocean[50],   // #F2AEBB - rose clair
        secondary: ocean[100], // #6DC3BB - teal clair
      },
      action: {
        hover: alpha(ocean[200], 0.2),
        selected: alpha(ocean[200], 0.3),
      },
      baseShadow:
        '0px 4px 16px 0px rgba(20, 22, 64, 0.7), 0px 8px 16px -5px rgba(20, 22, 64, 0.8)',
    },
  },
};

// ColorScheme White - Thème blanc minimaliste avec couleurs brand en accentuation
export const colorSchemesWhite = {
  light: {
    palette: {
      primary: {
        light: brand[200],
        main: brand[400],
        dark: brand[700],
        contrastText: brand[50],
      },
      secondary: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
      },
      info: {
        light: brand[100],
        main: brand[300],
        dark: brand[600],
        contrastText: gray[50],
      },
      warning: {
        light: orange[300],
        main: orange[400],
        dark: orange[800],
        contrastText: white[50],
      },
      error: {
        light: red[300],
        main: red[400],
        dark: red[800],
        contrastText: white[50],
      },
      success: {
        light: green[300],
        main: green[400],
        dark: green[800],
        contrastText: white[50],
      },
      grey: {
        ...white,
      },
      divider: alpha(white[300], 0.8),
      background: {
        default: white[50],  // Blanc pur
        paper: white[50],     // Blanc pur
      },
      text: {
        primary: white[900],   // Presque noir pour contraste maximal
        secondary: white[700],  // Gris foncé pour texte secondaire
      },
      action: {
        hover: alpha(white[900], 0.06),
        selected: alpha(white[900], 0.1),
      },
      baseShadow:
        '0px 1px 3px 0px rgba(0, 0, 0, 0.08), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: brand[50],
        light: brand[300],
        main: brand[400],
        dark: brand[700],
      },
      secondary: {
        contrastText: brand[300],
        light: brand[500],
        main: brand[700],
        dark: brand[900],
      },
      info: {
        contrastText: brand[300],
        light: brand[500],
        main: brand[700],
        dark: brand[900],
      },
      warning: {
        contrastText: white[50],
        light: orange[400],
        main: orange[500],
        dark: orange[700],
      },
      error: {
        contrastText: white[50],
        light: red[400],
        main: red[500],
        dark: red[700],
      },
      success: {
        contrastText: white[50],
        light: green[400],
        main: green[500],
        dark: green[700],
      },
      grey: {
        ...white,
      },
      divider: alpha(white[700], 0.3),
      background: {
        default: white[900],  // Noir pur
        paper: white[800],     // Presque noir
      },
      text: {
        primary: white[50],    // Blanc pur pour contraste maximal
        secondary: white[300], // Gris clair pour texte secondaire
      },
      action: {
        hover: alpha(white[50], 0.1),
        selected: alpha(white[50], 0.15),
      },
      baseShadow:
        '0px 4px 16px 0px rgba(0, 0, 0, 0.8), 0px 8px 16px -5px rgba(0, 0, 0, 0.9)',
    },
  },
};

// ColorScheme Black - Thème noir minimaliste dans les règles de l'art
export const colorSchemesBlack = {
  light: {
    palette: {
      primary: {
        light: black[700],
        main: black[900],
        dark: black[900],
        contrastText: black[50],
      },
      secondary: {
        light: black[600],
        main: black[800],
        dark: black[900],
        contrastText: black[50],
      },
      info: {
        light: black[600],
        main: black[800],
        dark: black[900],
        contrastText: black[50],
      },
      warning: {
        light: black[500],
        main: black[700],
        dark: black[800],
        contrastText: black[50],
      },
      error: {
        light: black[500],
        main: black[800],
        dark: black[900],
        contrastText: black[50],
      },
      success: {
        light: black[600],
        main: black[800],
        dark: black[900],
        contrastText: black[50],
      },
      grey: {
        ...black,
      },
      divider: alpha(black[300], 0.5),
      background: {
        default: black[50],  // Blanc cassé très léger
        paper: '#FFFFFF',     // Blanc pur
      },
      text: {
        primary: black[900],   // Noir pur pour contraste maximal
        secondary: black[700], // Gris foncé pour texte secondaire
      },
      action: {
        hover: alpha(black[900], 0.08),
        selected: alpha(black[900], 0.12),
      },
      baseShadow:
        '0px 2px 4px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px 0px rgba(0, 0, 0, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: black[50],
        light: black[200],
        main: black[100],
        dark: black[50],
      },
      secondary: {
        contrastText: black[50],
        light: black[100],
        main: black[200],
        dark: black[300],
      },
      info: {
        contrastText: black[50],
        light: black[100],
        main: black[200],
        dark: black[300],
      },
      warning: {
        contrastText: black[50],
        light: black[200],
        main: black[300],
        dark: black[400],
      },
      error: {
        contrastText: black[50],
        light: black[200],
        main: black[300],
        dark: black[400],
      },
      success: {
        contrastText: black[50],
        light: black[100],
        main: black[200],
        dark: black[300],
      },
      grey: {
        ...black,
      },
      divider: alpha(black[700], 0.4),
      background: {
        default: black[900],  // Noir pur
        paper: black[800],    // Presque noir
      },
      text: {
        primary: black[50],   // Blanc cassé pour contraste maximal
        secondary: black[200], // Gris clair pour texte secondaire
      },
      action: {
        hover: alpha(black[50], 0.12),
        selected: alpha(black[50], 0.18),
      },
      baseShadow:
        '0px 4px 20px 0px rgba(0, 0, 0, 0.9), 0px 8px 24px -5px rgba(0, 0, 0, 1)',
    },
  },
};

export const typography = {
  fontFamily: 'Inter, sans-serif',
  h1: {
    fontSize: defaultTheme.typography.pxToRem(48),
    fontWeight: 600,
    lineHeight: 1.2,
    letterSpacing: -0.5,
  },
  h2: {
    fontSize: defaultTheme.typography.pxToRem(36),
    fontWeight: 600,
    lineHeight: 1.2,
  },
  h3: {
    fontSize: defaultTheme.typography.pxToRem(30),
    lineHeight: 1.2,
  },
  h4: {
    fontSize: defaultTheme.typography.pxToRem(24),
    fontWeight: 600,
    lineHeight: 1.5,
  },
  h5: {
    fontSize: defaultTheme.typography.pxToRem(20),
    fontWeight: 600,
  },
  h6: {
    fontSize: defaultTheme.typography.pxToRem(18),
    fontWeight: 600,
  },
  subtitle1: {
    fontSize: defaultTheme.typography.pxToRem(18),
  },
  subtitle2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 500,
  },
  body1: {
    fontSize: defaultTheme.typography.pxToRem(14),
  },
  body2: {
    fontSize: defaultTheme.typography.pxToRem(14),
    fontWeight: 400,
  },
  caption: {
    fontSize: defaultTheme.typography.pxToRem(12),
    fontWeight: 400,
  },
};

export const shape = {
  borderRadius: 8,
};

// @ts-ignore
const defaultShadows: Shadows = [
  'none',
  'var(--template-palette-baseShadow)',
  ...defaultTheme.shadows.slice(2),
];
export const shadows = defaultShadows;

// Palette Sunset - Couleurs chaudes orange/rouge/rose
export const sunset = {
  50: '#FFF5F0',   // Crème très clair
  100: '#FFE8D6',  // Pêche clair
  200: '#FFD4B3',  // Orange clair
  300: '#FFB88C',  // Orange moyen
  400: '#FF8C5A',  // Orange vif
  500: '#FF6B35',  // Orange principal
  600: '#E55A2B',  // Orange foncé
  700: '#CC4A21',  // Rouge-orange
  800: '#B33A17',  // Rouge foncé
  900: '#8B2A0D',  // Rouge très foncé
};

// Palette Forest - Verts naturels et terreux
export const forest = {
  50: '#F0F7F4',   // Vert très clair
  100: '#D4E8DD',  // Vert clair
  200: '#A8D1B8',  // Vert moyen clair
  300: '#7CBA93',  // Vert moyen
  400: '#50A36E',  // Vert vif
  500: '#2D8651',  // Vert principal
  600: '#256A41',  // Vert foncé
  700: '#1D4E31',  // Vert très foncé
  800: '#153221',  // Vert presque noir
  900: '#0D1611',  // Presque noir
};

// Palette Midnight - Bleus profonds et violets
export const midnight = {
  50: '#F0F4FF',   // Bleu très clair
  100: '#D6E3FF',  // Bleu clair
  200: '#ADC7FF',  // Bleu moyen clair
  300: '#84ABFF',  // Bleu moyen
  400: '#5B8FFF',  // Bleu vif
  500: '#1E3A8A',  // Bleu principal (navy)
  600: '#1E40AF',  // Bleu foncé
  700: '#1E3A8A',  // Navy
  800: '#172554',  // Navy très foncé
  900: '#0F172A',  // Presque noir bleu
};

// Palette Aurora - Violets et magentas vibrants
export const aurora = {
  50: '#F5F3FF',   // Violet très clair
  100: '#E9E5FF',  // Violet clair
  200: '#D3CBFF',  // Violet moyen clair
  300: '#BDB1FF',  // Violet moyen
  400: '#A797FF',  // Violet vif
  500: '#8B5CF6',  // Violet principal
  600: '#7C3AED',  // Violet foncé
  700: '#6D28D9',  // Violet très foncé
  800: '#5B21B6',  // Violet profond
  900: '#4C1D95',  // Violet presque noir
};

// Palette Amber - Ors et ambrés chaleureux
export const amber = {
  50: '#FFFBEB',   // Jaune très clair
  100: '#FEF3C7',  // Jaune clair
  200: '#FDE68A',  // Jaune moyen clair
  300: '#FCD34D',  // Jaune moyen
  400: '#FBBF24',  // Jaune vif
  500: '#F59E0B',  // Ambre principal
  600: '#D97706',  // Ambre foncé
  700: '#B45309',  // Ambre très foncé
  800: '#92400E',  // Ambre profond
  900: '#78350F',  // Ambre presque noir
};

// Palette Emerald - Verts émeraude élégants
export const emerald = {
  50: '#ECFDF5',   // Vert émeraude très clair
  100: '#D1FAE5',  // Vert émeraude clair
  200: '#A7F3D0',  // Vert émeraude moyen clair
  300: '#6EE7B7',  // Vert émeraude moyen
  400: '#34D399',  // Vert émeraude vif
  500: '#10B981',  // Vert émeraude principal
  600: '#059669',  // Vert émeraude foncé
  700: '#047857',  // Vert émeraude très foncé
  800: '#065F46',  // Vert émeraude profond
  900: '#064E3B',  // Vert émeraude presque noir
};

// Palette MUI Official - Couleurs exactes du site mui.com
// Reproduit le design system officiel de MUI avec leur couleur signature #007FFF
export const muiBlue = {
  50: '#F0F7FF',   // Bleu très clair (backgrounds subtils)
  100: '#C2E0FF',  // Bleu clair
  200: '#99CCF3',  // Bleu moyen clair
  300: '#66B2FF',  // Bleu moyen
  400: '#3399FF',  // Bleu vif
  500: '#007FFF',  // MUI Blue signature - couleur principale
  600: '#0072E5',  // Bleu foncé
  700: '#0059B2',  // Bleu très foncé
  800: '#004C99',  // Bleu profond
  900: '#003A75',  // Bleu presque noir
};

// Gris pour le thème MUI Official
export const muiGray = {
  50: '#F3F6F9',   // Gris très clair bleuté (background secondaire en mode clair)
  100: '#E7EBF0',  // Gris clair
  200: '#E0E3E7',  // Gris moyen clair
  300: '#CDD2D7',  // Gris moyen
  400: '#B2BAC2',  // Gris pour texte secondaire en mode sombre
  500: '#A0AAB4',  // Gris moyen
  600: '#6F7E8C',  // Gris foncé
  700: '#3E5060',  // Gris très foncé (texte secondaire en mode clair)
  800: '#2D3843',  // Gris presque noir
  900: '#1A2027',  // Presque noir (texte principal en mode clair)
};

// Couleurs de fond pour le mode sombre MUI
export const muiDark = {
  50: '#E2EDF8',   // Très clair (pour contrastes)
  100: '#CEE0F3',  // Clair
  200: '#91B9E3',  // Moyen clair
  300: '#5090D3',  // Moyen
  400: '#265D97',  // Foncé
  500: '#1E4976',  // Très foncé
  600: '#173A5E',  // Profond
  700: '#132F4C',  // Background paper en mode sombre
  800: '#0A1929',  // Background principal en mode sombre
  900: '#001E3C',  // Le plus foncé
};

// ==========================================
// Slate Blue+ (FITD) - Corporate & Elegant / Premium & Tech
// ==========================================
export const colorSchemesSlateBluePlus = {
  light: {
    palette: {
      primary: {
        main: '#3B5998', // Slate Blue+
        light: '#4C70BA',
        dark: '#2E4A7C',
        contrastText: '#FFFFFF',
      },
      grey: {
        ...gray,
      },
      background: {
        default: '#F1F5F9',
        paper: '#FFFFFF',
      },
      divider: '#E2E8F0',
      text: {
        primary: '#0F172A',
        secondary: '#64748B',
      },
      action: {
        hover: alpha('#3B5998', 0.04),
        selected: alpha('#3B5998', 0.08),
      },
      baseShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    },
  },
  dark: {
    palette: {
      primary: {
        main: '#FFFFFF',
        light: '#F1F5F9',
        dark: '#E2E8F0',
        contrastText: '#3B5998', // Slate Blue+ for button text
      },
      grey: {
        ...gray,
      },
      background: {
        default: '#0F172A',
        paper: '#1E293B',
      },
      divider: '#334155',
      text: {
        primary: '#F1F5F9',
        secondary: '#94A3B8',
      },
      action: {
        hover: alpha('#FFFFFF', 0.04),
        selected: alpha('#FFFFFF', 0.08),
      },
      baseShadow: 'none',
    },
  },
};

// ColorScheme Sunset
export const colorSchemesSunset = {
  light: {
    palette: {
      primary: {
        light: sunset[200],
        main: sunset[500],
        dark: sunset[700],
        contrastText: sunset[50],
      },
      secondary: {
        light: sunset[100],
        main: sunset[300],
        dark: sunset[600],
        contrastText: sunset[900],
      },
      info: {
        light: sunset[100],
        main: sunset[300],
        dark: sunset[600],
        contrastText: sunset[900],
      },
      warning: {
        light: sunset[200],
        main: sunset[400],
        dark: sunset[600],
        contrastText: sunset[900],
      },
      error: {
        light: sunset[200],
        main: sunset[500],
        dark: sunset[700],
        contrastText: sunset[50],
      },
      success: {
        light: sunset[100],
        main: sunset[300],
        dark: sunset[600],
        contrastText: sunset[900],
      },
      grey: {
        ...sunset,
      },
      divider: alpha(sunset[200], 0.5),
      background: {
        default: sunset[50],
        paper: '#FFFFFF',
      },
      text: {
        primary: sunset[900],
        secondary: sunset[600],
      },
      action: {
        hover: alpha(sunset[400], 0.08),
        selected: alpha(sunset[400], 0.12),
      },
      baseShadow: '0px 1px 3px 0px rgba(255, 107, 53, 0.1), 0px 1px 2px 0px rgba(255, 107, 53, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: sunset[50],
        light: sunset[300],
        main: sunset[500],
        dark: sunset[700],
      },
      secondary: {
        contrastText: sunset[50],
        light: sunset[200],
        main: sunset[400],
        dark: sunset[600],
      },
      info: {
        contrastText: sunset[50],
        light: sunset[200],
        main: sunset[400],
        dark: sunset[600],
      },
      warning: {
        contrastText: sunset[50],
        light: sunset[300],
        main: sunset[500],
        dark: sunset[700],
      },
      error: {
        contrastText: sunset[50],
        light: sunset[400],
        main: sunset[500],
        dark: sunset[700],
      },
      success: {
        contrastText: sunset[50],
        light: sunset[200],
        main: sunset[400],
        dark: sunset[600],
      },
      grey: {
        ...sunset,
      },
      divider: alpha(sunset[600], 0.6),
      background: {
        default: sunset[900],
        paper: sunset[800],
      },
      text: {
        primary: sunset[50],
        secondary: sunset[200],
      },
      action: {
        hover: alpha(sunset[500], 0.2),
        selected: alpha(sunset[500], 0.3),
      },
      baseShadow: '0px 4px 16px 0px rgba(139, 42, 13, 0.7), 0px 8px 16px -5px rgba(139, 42, 13, 0.8)',
    },
  },
};

// ColorScheme Forest
export const colorSchemesForest = {
  light: {
    palette: {
      primary: {
        light: forest[200],
        main: forest[500],
        dark: forest[700],
        contrastText: forest[50],
      },
      secondary: {
        light: forest[100],
        main: forest[300],
        dark: forest[600],
        contrastText: forest[900],
      },
      info: {
        light: forest[100],
        main: forest[300],
        dark: forest[600],
        contrastText: forest[900],
      },
      warning: {
        light: forest[200],
        main: forest[400],
        dark: forest[600],
        contrastText: forest[900],
      },
      error: {
        light: forest[200],
        main: forest[500],
        dark: forest[700],
        contrastText: forest[50],
      },
      success: {
        light: forest[100],
        main: forest[500],
        dark: forest[700],
        contrastText: forest[50],
      },
      grey: {
        ...forest,
      },
      divider: alpha(forest[200], 0.5),
      background: {
        default: forest[50],
        paper: '#FFFFFF',
      },
      text: {
        primary: forest[900],
        secondary: forest[600],
      },
      action: {
        hover: alpha(forest[400], 0.08),
        selected: alpha(forest[400], 0.12),
      },
      baseShadow: '0px 1px 3px 0px rgba(45, 134, 81, 0.1), 0px 1px 2px 0px rgba(45, 134, 81, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: forest[50],
        light: forest[300],
        main: forest[500],
        dark: forest[700],
      },
      secondary: {
        contrastText: forest[50],
        light: forest[200],
        main: forest[400],
        dark: forest[600],
      },
      info: {
        contrastText: forest[50],
        light: forest[200],
        main: forest[400],
        dark: forest[600],
      },
      warning: {
        contrastText: forest[50],
        light: forest[300],
        main: forest[500],
        dark: forest[700],
      },
      error: {
        contrastText: forest[50],
        light: forest[400],
        main: forest[500],
        dark: forest[700],
      },
      success: {
        contrastText: forest[50],
        light: forest[300],
        main: forest[500],
        dark: forest[700],
      },
      grey: {
        ...forest,
      },
      divider: alpha(forest[600], 0.6),
      background: {
        default: forest[900],
        paper: forest[800],
      },
      text: {
        primary: forest[50],
        secondary: forest[200],
      },
      action: {
        hover: alpha(forest[500], 0.2),
        selected: alpha(forest[500], 0.3),
      },
      baseShadow: '0px 4px 16px 0px rgba(13, 22, 17, 0.7), 0px 8px 16px -5px rgba(13, 22, 17, 0.8)',
    },
  },
};

// ColorScheme Midnight
export const colorSchemesMidnight = {
  light: {
    palette: {
      primary: {
        light: midnight[200],
        main: midnight[500],
        dark: midnight[700],
        contrastText: midnight[50],
      },
      secondary: {
        light: midnight[100],
        main: midnight[300],
        dark: midnight[600],
        contrastText: midnight[900],
      },
      info: {
        light: midnight[100],
        main: midnight[300],
        dark: midnight[600],
        contrastText: midnight[50],
      },
      warning: {
        light: midnight[200],
        main: midnight[400],
        dark: midnight[600],
        contrastText: midnight[900],
      },
      error: {
        light: midnight[200],
        main: midnight[500],
        dark: midnight[700],
        contrastText: midnight[50],
      },
      success: {
        light: midnight[100],
        main: midnight[300],
        dark: midnight[600],
        contrastText: midnight[900],
      },
      grey: {
        ...midnight,
      },
      divider: alpha(midnight[200], 0.5),
      background: {
        default: midnight[50],
        paper: '#FFFFFF',
      },
      text: {
        primary: midnight[900],
        secondary: midnight[600],
      },
      action: {
        hover: alpha(midnight[400], 0.08),
        selected: alpha(midnight[400], 0.12),
      },
      baseShadow: '0px 1px 3px 0px rgba(30, 58, 138, 0.1), 0px 1px 2px 0px rgba(30, 58, 138, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: midnight[50],
        light: midnight[300],
        main: midnight[500],
        dark: midnight[700],
      },
      secondary: {
        contrastText: midnight[50],
        light: midnight[200],
        main: midnight[400],
        dark: midnight[600],
      },
      info: {
        contrastText: midnight[50],
        light: midnight[200],
        main: midnight[400],
        dark: midnight[600],
      },
      warning: {
        contrastText: midnight[50],
        light: midnight[300],
        main: midnight[500],
        dark: midnight[700],
      },
      error: {
        contrastText: midnight[50],
        light: midnight[400],
        main: midnight[500],
        dark: midnight[700],
      },
      success: {
        contrastText: midnight[50],
        light: midnight[200],
        main: midnight[400],
        dark: midnight[600],
      },
      grey: {
        ...midnight,
      },
      divider: alpha(midnight[600], 0.6),
      background: {
        default: midnight[900],
        paper: midnight[800],
      },
      text: {
        primary: midnight[50],
        secondary: midnight[200],
      },
      action: {
        hover: alpha(midnight[500], 0.2),
        selected: alpha(midnight[500], 0.3),
      },
      baseShadow: '0px 4px 16px 0px rgba(15, 23, 42, 0.7), 0px 8px 16px -5px rgba(15, 23, 42, 0.8)',
    },
  },
};

// ColorScheme Aurora
export const colorSchemesAurora = {
  light: {
    palette: {
      primary: {
        light: aurora[200],
        main: aurora[500],
        dark: aurora[700],
        contrastText: aurora[50],
      },
      secondary: {
        light: aurora[100],
        main: aurora[300],
        dark: aurora[600],
        contrastText: aurora[900],
      },
      info: {
        light: aurora[100],
        main: aurora[300],
        dark: aurora[600],
        contrastText: aurora[50],
      },
      warning: {
        light: aurora[200],
        main: aurora[400],
        dark: aurora[600],
        contrastText: aurora[900],
      },
      error: {
        light: aurora[200],
        main: aurora[500],
        dark: aurora[700],
        contrastText: aurora[50],
      },
      success: {
        light: aurora[100],
        main: aurora[300],
        dark: aurora[600],
        contrastText: aurora[900],
      },
      grey: {
        ...aurora,
      },
      divider: alpha(aurora[200], 0.5),
      background: {
        default: aurora[50],
        paper: '#FFFFFF',
      },
      text: {
        primary: aurora[900],
        secondary: aurora[600],
      },
      action: {
        hover: alpha(aurora[400], 0.08),
        selected: alpha(aurora[400], 0.12),
      },
      baseShadow: '0px 1px 3px 0px rgba(139, 92, 246, 0.1), 0px 1px 2px 0px rgba(139, 92, 246, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: aurora[50],
        light: aurora[300],
        main: aurora[500],
        dark: aurora[700],
      },
      secondary: {
        contrastText: aurora[50],
        light: aurora[200],
        main: aurora[400],
        dark: aurora[600],
      },
      info: {
        contrastText: aurora[50],
        light: aurora[200],
        main: aurora[400],
        dark: aurora[600],
      },
      warning: {
        contrastText: aurora[50],
        light: aurora[300],
        main: aurora[500],
        dark: aurora[700],
      },
      error: {
        contrastText: aurora[50],
        light: aurora[400],
        main: aurora[500],
        dark: aurora[700],
      },
      success: {
        contrastText: aurora[50],
        light: aurora[200],
        main: aurora[400],
        dark: aurora[600],
      },
      grey: {
        ...aurora,
      },
      divider: alpha(aurora[600], 0.6),
      background: {
        default: aurora[900],
        paper: aurora[800],
      },
      text: {
        primary: aurora[50],
        secondary: aurora[200],
      },
      action: {
        hover: alpha(aurora[500], 0.2),
        selected: alpha(aurora[500], 0.3),
      },
      baseShadow: '0px 4px 16px 0px rgba(76, 29, 149, 0.7), 0px 8px 16px -5px rgba(76, 29, 149, 0.8)',
    },
  },
};

// ColorScheme Amber
export const colorSchemesAmber = {
  light: {
    palette: {
      primary: {
        light: amber[200],
        main: amber[500],
        dark: amber[700],
        contrastText: amber[50],
      },
      secondary: {
        light: amber[100],
        main: amber[300],
        dark: amber[600],
        contrastText: amber[900],
      },
      info: {
        light: amber[100],
        main: amber[300],
        dark: amber[600],
        contrastText: amber[900],
      },
      warning: {
        light: amber[200],
        main: amber[400],
        dark: amber[600],
        contrastText: amber[900],
      },
      error: {
        light: amber[200],
        main: amber[500],
        dark: amber[700],
        contrastText: amber[50],
      },
      success: {
        light: amber[100],
        main: amber[300],
        dark: amber[600],
        contrastText: amber[900],
      },
      grey: {
        ...amber,
      },
      divider: alpha(amber[200], 0.5),
      background: {
        default: amber[50],
        paper: '#FFFFFF',
      },
      text: {
        primary: amber[900],
        secondary: amber[600],
      },
      action: {
        hover: alpha(amber[400], 0.08),
        selected: alpha(amber[400], 0.12),
      },
      baseShadow: '0px 1px 3px 0px rgba(245, 158, 11, 0.1), 0px 1px 2px 0px rgba(245, 158, 11, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: amber[50],
        light: amber[300],
        main: amber[500],
        dark: amber[700],
      },
      secondary: {
        contrastText: amber[50],
        light: amber[200],
        main: amber[400],
        dark: amber[600],
      },
      info: {
        contrastText: amber[50],
        light: amber[200],
        main: amber[400],
        dark: amber[600],
      },
      warning: {
        contrastText: amber[50],
        light: amber[300],
        main: amber[500],
        dark: amber[700],
      },
      error: {
        contrastText: amber[50],
        light: amber[400],
        main: amber[500],
        dark: amber[700],
      },
      success: {
        contrastText: amber[50],
        light: amber[200],
        main: amber[400],
        dark: amber[600],
      },
      grey: {
        ...amber,
      },
      divider: alpha(amber[600], 0.6),
      background: {
        default: amber[900],
        paper: amber[800],
      },
      text: {
        primary: amber[50],
        secondary: amber[200],
      },
      action: {
        hover: alpha(amber[500], 0.2),
        selected: alpha(amber[500], 0.3),
      },
      baseShadow: '0px 4px 16px 0px rgba(120, 53, 15, 0.7), 0px 8px 16px -5px rgba(120, 53, 15, 0.8)',
    },
  },
};

// ColorScheme Emerald
export const colorSchemesEmerald = {
  light: {
    palette: {
      primary: {
        light: emerald[200],
        main: emerald[500],
        dark: emerald[700],
        contrastText: emerald[50],
      },
      secondary: {
        light: emerald[100],
        main: emerald[300],
        dark: emerald[600],
        contrastText: emerald[900],
      },
      info: {
        light: emerald[100],
        main: emerald[300],
        dark: emerald[600],
        contrastText: emerald[50],
      },
      warning: {
        light: emerald[200],
        main: emerald[400],
        dark: emerald[600],
        contrastText: emerald[900],
      },
      error: {
        light: emerald[200],
        main: emerald[500],
        dark: emerald[700],
        contrastText: emerald[50],
      },
      success: {
        light: emerald[100],
        main: emerald[500],
        dark: emerald[700],
        contrastText: emerald[50],
      },
      grey: {
        ...emerald,
      },
      divider: alpha(emerald[200], 0.5),
      background: {
        default: emerald[50],
        paper: '#FFFFFF',
      },
      text: {
        primary: emerald[900],
        secondary: emerald[600],
      },
      action: {
        hover: alpha(emerald[400], 0.08),
        selected: alpha(emerald[400], 0.12),
      },
      baseShadow: '0px 1px 3px 0px rgba(16, 185, 129, 0.1), 0px 1px 2px 0px rgba(16, 185, 129, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: {
        contrastText: emerald[50],
        light: emerald[300],
        main: emerald[500],
        dark: emerald[700],
      },
      secondary: {
        contrastText: emerald[50],
        light: emerald[200],
        main: emerald[400],
        dark: emerald[600],
      },
      info: {
        contrastText: emerald[50],
        light: emerald[200],
        main: emerald[400],
        dark: emerald[600],
      },
      warning: {
        contrastText: emerald[50],
        light: emerald[300],
        main: emerald[500],
        dark: emerald[700],
      },
      error: {
        contrastText: emerald[50],
        light: emerald[400],
        main: emerald[500],
        dark: emerald[700],
      },
      success: {
        contrastText: emerald[50],
        light: emerald[300],
        main: emerald[500],
        dark: emerald[700],
      },
      grey: {
        ...emerald,
      },
      divider: alpha(emerald[600], 0.6),
      background: {
        default: emerald[900],
        paper: emerald[800],
      },
      text: {
        primary: emerald[50],
        secondary: emerald[200],
      },
      action: {
        hover: alpha(emerald[500], 0.2),
        selected: alpha(emerald[500], 0.3),
      },
      baseShadow: '0px 4px 16px 0px rgba(6, 78, 59, 0.7), 0px 8px 16px -5px rgba(6, 78, 59, 0.8)',
    },
  },
};

// ColorScheme MUI Official - Design system exact du site mui.com
export const colorSchemesMuiOfficial = {
  light: {
    palette: {
      primary: {
        light: muiBlue[300],
        main: muiBlue[500],  // #007FFF - couleur signature MUI
        dark: muiBlue[700],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: muiGray[300],
        main: muiGray[500],
        dark: muiGray[700],
        contrastText: '#FFFFFF',
      },
      info: {
        light: muiBlue[100],
        main: muiBlue[500],
        dark: muiBlue[700],
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#FFE999',
        main: '#FFC107',
        dark: '#FF9800',
        contrastText: muiGray[900],
      },
      error: {
        light: '#FF6B6B',
        main: '#F44336',
        dark: '#D32F2F',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#81C784',
        main: '#4CAF50',
        dark: '#388E3C',
        contrastText: '#FFFFFF',
      },
      grey: {
        ...muiGray,
      },
      divider: alpha(muiBlue[100], 0.08),
      background: {
        default: '#FFFFFF',        // Blanc pur comme sur mui.com
        paper: muiGray[50],        // Gris très clair pour les cards
      },
      text: {
        primary: muiGray[900],     // #1A2027 - texte principal
        secondary: muiGray[700],   // #3E5060 - texte secondaire
      },
      action: {
        hover: alpha(muiBlue[500], 0.04),
        selected: alpha(muiBlue[500], 0.08),
        disabled: alpha(muiGray[900], 0.26),
        disabledBackground: alpha(muiGray[900], 0.12),
      },
      baseShadow: '0px 2px 4px rgba(170, 180, 190, 0.3)',
    },
  },
  dark: {
    palette: {
      primary: {
        light: muiBlue[300],
        main: muiBlue[500],  // #007FFF - couleur signature MUI
        dark: muiBlue[700],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: muiGray[300],
        main: muiGray[500],
        dark: muiGray[700],
        contrastText: '#FFFFFF',
      },
      info: {
        light: muiBlue[300],
        main: muiBlue[500],
        dark: muiBlue[700],
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#FFE57F',
        main: '#FFD54F',
        dark: '#FFC107',
        contrastText: muiGray[900],
      },
      error: {
        light: '#FF6B6B',
        main: '#F44336',
        dark: '#D32F2F',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#81C784',
        main: '#66BB6A',
        dark: '#4CAF50',
        contrastText: '#000000',
      },
      grey: {
        ...muiGray,
      },
      divider: alpha(muiBlue[100], 0.08),
      background: {
        default: muiDark[800],     // #0A1929 - fond principal sombre
        paper: muiDark[700],       // #132F4C - cards/surfaces
      },
      text: {
        primary: '#FFFFFF',        // Blanc pur
        secondary: muiGray[400],   // #B2BAC2 - texte secondaire
      },
      action: {
        hover: alpha(muiBlue[500], 0.08),
        selected: alpha(muiBlue[500], 0.16),
        disabled: alpha(muiGray[50], 0.3),
        disabledBackground: alpha(muiGray[50], 0.12),
      },
      baseShadow: '0px 4px 20px rgba(0, 0, 0, 0.5)',
    },
  },
};

// Premium Dark - Thème sombre professionnel avec accents orange/pêche
export const colorSchemesPremium = {
  light: {
    palette: {
      primary: {
        light: premium[300],
        main: premium[500],     // #F5A962 - orange pêche principal
        dark: premium[700],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: premium[200],
        main: premium[400],
        dark: premium[600],
        contrastText: '#FFFFFF',
      },
      info: {
        light: premium[300],
        main: premium[500],
        dark: premium[700],
        contrastText: '#FFFFFF',
      },
      warning: {
        light: premium[200],
        main: premium[400],
        dark: premium[600],
        contrastText: premiumGray[900],
      },
      error: {
        light: '#FF6B6B',
        main: '#F44336',
        dark: '#D32F2F',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#81C784',
        main: '#66BB6A',
        dark: '#4CAF50',
        contrastText: '#000000',
      },
      grey: {
        ...premiumGray,
      },
      divider: alpha(premiumGray[400], 0.2),
      background: {
        default: premiumGray[900],  // #1A1B1E - background principal très sombre
        paper: premiumGray[700],    // #27282C - cartes/surfaces
      },
      text: {
        primary: '#FFFFFF',          // Blanc pur
        secondary: premiumGray[200], // #9CA3AF - gris moyen pour texte secondaire
      },
      action: {
        hover: alpha(premium[500], 0.08),
        selected: alpha(premium[500], 0.12),
        disabled: alpha('#FFFFFF', 0.3),
        disabledBackground: alpha('#FFFFFF', 0.08),
      },
      baseShadow: '0px 4px 20px rgba(0, 0, 0, 0.6), 0px 8px 24px -5px rgba(0, 0, 0, 0.8)',
    },
  },
  dark: {
    palette: {
      primary: {
        light: premium[300],
        main: premium[500],
        dark: premium[700],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: premium[200],
        main: premium[400],
        dark: premium[600],
        contrastText: '#FFFFFF',
      },
      info: {
        light: premium[300],
        main: premium[500],
        dark: premium[700],
        contrastText: '#FFFFFF',
      },
      warning: {
        light: premium[200],
        main: premium[400],
        dark: premium[600],
        contrastText: premiumGray[900],
      },
      error: {
        light: '#FF6B6B',
        main: '#F44336',
        dark: '#D32F2F',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#81C784',
        main: '#66BB6A',
        dark: '#4CAF50',
        contrastText: '#000000',
      },
      grey: {
        ...premiumGray,
      },
      divider: alpha(premiumGray[400], 0.2),
      background: {
        default: premiumGray[900],  // #1A1B1E - background principal très sombre
        paper: premiumGray[700],    // #27282C - cartes/surfaces
      },
      text: {
        primary: '#FFFFFF',          // Blanc pur
        secondary: premiumGray[200], // #9CA3AF - gris moyen
      },
      action: {
        hover: alpha(premium[500], 0.08),
        selected: alpha(premium[500], 0.12),
        disabled: alpha('#FFFFFF', 0.3),
        disabledBackground: alpha('#FFFFFF', 0.08),
      },
      baseShadow: '0px 4px 20px rgba(0, 0, 0, 0.6), 0px 8px 24px -5px rgba(0, 0, 0, 0.8)',
    },
  },
};

// =============================================================================
// WARM SUCCESS - Design System Theme
// =============================================================================

// Warm Success Primary (Forest Green)
export const warmSuccessPrimary = {
  50: '#F0FDF4',
  100: '#DCFCE7',
  200: '#BBF7D0',
  300: '#86EFAC',
  400: '#4ADE80',
  500: '#22C55E',
  600: '#16A34A',
  700: '#166534',
  800: '#14532D',
  900: '#052E16',
};

// Warm Success Secondary (Warm Amber)
export const warmSuccessSecondary = {
  50: '#FFFBEB',
  100: '#FEF3C7',
  200: '#FDE68A',
  300: '#FCD34D',
  400: '#FBBF24',
  500: '#F59E0B',
  600: '#D97706',
  700: '#B45309',
  800: '#92400E',
  900: '#78350F',
};

// Warm Success Accent (Warm Orange)
export const warmSuccessAccent = {
  50: '#FFF7ED',
  100: '#FFEDD5',
  200: '#FED7AA',
  300: '#FDBA74',
  400: '#FB923C',
  500: '#F97316',
  600: '#EA580C',
  700: '#C2410C',
  800: '#9A3412',
  900: '#7C2D12',
};

// Warm Success Neutral (Warm Stone)
export const warmSuccessNeutral = {
  50: '#FAFAF9',
  100: '#F5F5F4',
  200: '#E7E5E4',
  300: '#D6D3D1',
  400: '#A8A29E',
  500: '#78716C',
  600: '#57534E',
  700: '#44403C',
  800: '#292524',
  900: '#1C1917',
};

// ColorScheme Warm Success
export const colorSchemesWarmSuccess = {
  light: {
    palette: {
      primary: {
        light: warmSuccessPrimary[400],
        main: warmSuccessPrimary[700],
        dark: warmSuccessPrimary[800],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: warmSuccessSecondary[300],
        main: warmSuccessSecondary[500],
        dark: warmSuccessSecondary[700],
        contrastText: warmSuccessNeutral[900],
      },
      info: {
        light: '#BFDBFE',
        main: '#3B82F6',
        dark: '#1D4ED8',
        contrastText: '#FFFFFF',
      },
      warning: {
        light: warmSuccessSecondary[200],
        main: warmSuccessSecondary[500],
        dark: warmSuccessSecondary[700],
        contrastText: warmSuccessNeutral[900],
      },
      error: {
        light: '#FECACA',
        main: '#EF4444',
        dark: '#B91C1C',
        contrastText: '#FFFFFF',
      },
      success: {
        light: warmSuccessPrimary[300],
        main: warmSuccessPrimary[500],
        dark: warmSuccessPrimary[700],
        contrastText: '#FFFFFF',
      },
      grey: {
        ...warmSuccessNeutral,
      },
      divider: alpha(warmSuccessNeutral[200], 0.8),
      background: {
        default: '#FFFBF5',  // Warm cream
        paper: '#FFFFFF',
      },
      text: {
        primary: warmSuccessNeutral[900],
        secondary: warmSuccessNeutral[600],
      },
      action: {
        hover: alpha(warmSuccessPrimary[700], 0.06),
        selected: alpha(warmSuccessPrimary[700], 0.1),
      },
      baseShadow: '0px 4px 6px -1px rgba(28, 25, 23, 0.07), 0px 2px 4px -2px rgba(28, 25, 23, 0.05)',
    },
  },
  dark: {
    palette: {
      primary: {
        light: warmSuccessPrimary[400],
        main: warmSuccessPrimary[500],
        dark: warmSuccessPrimary[700],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: warmSuccessSecondary[400],
        main: warmSuccessSecondary[500],
        dark: warmSuccessSecondary[600],
        contrastText: warmSuccessNeutral[900],
      },
      info: {
        light: '#93C5FD',
        main: '#60A5FA',
        dark: '#3B82F6',
        contrastText: '#FFFFFF',
      },
      warning: {
        light: warmSuccessSecondary[300],
        main: warmSuccessSecondary[400],
        dark: warmSuccessSecondary[600],
        contrastText: warmSuccessNeutral[900],
      },
      error: {
        light: '#FCA5A5',
        main: '#F87171',
        dark: '#EF4444',
        contrastText: '#FFFFFF',
      },
      success: {
        light: warmSuccessPrimary[400],
        main: warmSuccessPrimary[500],
        dark: warmSuccessPrimary[600],
        contrastText: '#FFFFFF',
      },
      grey: {
        ...warmSuccessNeutral,
      },
      divider: alpha(warmSuccessNeutral[700], 0.5),
      background: {
        default: warmSuccessNeutral[900],
        paper: warmSuccessNeutral[800],
      },
      text: {
        primary: warmSuccessNeutral[50],
        secondary: warmSuccessNeutral[400],
      },
      action: {
        hover: alpha(warmSuccessPrimary[500], 0.15),
        selected: alpha(warmSuccessPrimary[500], 0.25),
      },
      baseShadow: '0px 4px 16px 0px rgba(5, 46, 22, 0.5), 0px 8px 16px -5px rgba(5, 46, 22, 0.6)',
    },
  },
};

// ============================================================================
// MIDNIGHT LUXE - Sophistiqué, premium, exclusif
// ============================================================================
export const midnightLuxePrimary = {
  50: '#FAFAFA',
  100: '#F4F4F5',
  200: '#E4E4E7',
  300: '#D4D4D8',
  400: '#A1A1AA',
  500: '#71717A',
  600: '#52525B',
  700: '#3F3F46',
  800: '#27272A',
  900: '#18181B',
};

export const midnightLuxeAccent = {
  50: '#FEFCE8',
  100: '#FEF9C3',
  200: '#FEF08A',
  300: '#FDE047',
  400: '#FACC15',
  500: '#EAB308',
  600: '#CA8A04',
  700: '#A16207',
  800: '#854D0E',
  900: '#713F12',
};

export const colorSchemesMidnightLuxe = {
  light: {
    palette: {
      primary: {
        light: midnightLuxePrimary[600],
        main: midnightLuxePrimary[800],
        dark: midnightLuxePrimary[900],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: midnightLuxeAccent[400],
        main: midnightLuxeAccent[500],
        dark: midnightLuxeAccent[600],
        contrastText: midnightLuxePrimary[900],
      },
      info: {
        light: '#BFDBFE',
        main: '#3B82F6',
        dark: '#1D4ED8',
        contrastText: '#FFFFFF',
      },
      warning: {
        light: midnightLuxeAccent[200],
        main: midnightLuxeAccent[500],
        dark: midnightLuxeAccent[700],
        contrastText: midnightLuxePrimary[900],
      },
      error: {
        light: '#FECACA',
        main: '#EF4444',
        dark: '#B91C1C',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#BBF7D0',
        main: '#10B981',
        dark: '#047857',
        contrastText: '#FFFFFF',
      },
      grey: {
        ...midnightLuxePrimary,
      },
      divider: alpha(midnightLuxePrimary[300], 0.5),
      background: {
        default: '#FAFAFA',
        paper: '#FFFFFF',
      },
      text: {
        primary: midnightLuxePrimary[900],
        secondary: midnightLuxePrimary[600],
      },
      action: {
        hover: alpha(midnightLuxePrimary[800], 0.04),
        selected: alpha(midnightLuxePrimary[800], 0.08),
      },
      baseShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
    },
  },
  dark: {
    palette: {
      primary: {
        light: midnightLuxePrimary[200],
        main: '#FAFAFA',
        dark: midnightLuxePrimary[100],
        contrastText: midnightLuxePrimary[900],
      },
      secondary: {
        light: midnightLuxeAccent[300],
        main: midnightLuxeAccent[400],
        dark: midnightLuxeAccent[500],
        contrastText: midnightLuxePrimary[900],
      },
      info: {
        light: '#93C5FD',
        main: '#60A5FA',
        dark: '#3B82F6',
        contrastText: '#FFFFFF',
      },
      warning: {
        light: midnightLuxeAccent[300],
        main: midnightLuxeAccent[400],
        dark: midnightLuxeAccent[600],
        contrastText: midnightLuxePrimary[900],
      },
      error: {
        light: '#FCA5A5',
        main: '#F87171',
        dark: '#EF4444',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#6EE7B7',
        main: '#34D399',
        dark: '#10B981',
        contrastText: '#FFFFFF',
      },
      grey: {
        ...midnightLuxePrimary,
      },
      divider: alpha(midnightLuxePrimary[700], 0.5),
      background: {
        default: '#09090B',
        paper: midnightLuxePrimary[900],
      },
      text: {
        primary: '#FAFAFA',
        secondary: midnightLuxePrimary[400],
      },
      action: {
        hover: alpha(midnightLuxePrimary[100], 0.08),
        selected: alpha(midnightLuxePrimary[100], 0.12),
      },
      baseShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    },
  },
};

// ============================================================================
// AURORA FRESH - Vivant, moderne, dynamique
// ============================================================================
export const auroraFreshPrimary = {
  50: '#F5F3FF',
  100: '#EDE9FE',
  200: '#DDD6FE',
  300: '#C4B5FD',
  400: '#A78BFA',
  500: '#8B5CF6',
  600: '#7C3AED',
  700: '#6D28D9',
  800: '#5B21B6',
  900: '#4C1D95',
};

export const auroraFreshAccent = {
  50: '#ECFEFF',
  100: '#CFFAFE',
  200: '#A5F3FC',
  300: '#67E8F9',
  400: '#22D3EE',
  500: '#06B6D4',
  600: '#0891B2',
  700: '#0E7490',
  800: '#155E75',
  900: '#164E63',
};

export const auroraFreshNeutral = {
  50: '#F8FAFC',
  100: '#F1F5F9',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A',
};

export const colorSchemesAuroraFresh = {
  light: {
    palette: {
      primary: {
        light: auroraFreshPrimary[400],
        main: auroraFreshPrimary[600],
        dark: auroraFreshPrimary[700],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: auroraFreshAccent[400],
        main: auroraFreshAccent[500],
        dark: auroraFreshAccent[600],
        contrastText: '#FFFFFF',
      },
      info: {
        light: auroraFreshAccent[300],
        main: auroraFreshAccent[500],
        dark: auroraFreshAccent[700],
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#FDE68A',
        main: '#F59E0B',
        dark: '#D97706',
        contrastText: '#000000',
      },
      error: {
        light: '#FECACA',
        main: '#EF4444',
        dark: '#B91C1C',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#A7F3D0',
        main: '#10B981',
        dark: '#047857',
        contrastText: '#FFFFFF',
      },
      grey: {
        ...auroraFreshNeutral,
      },
      divider: alpha(auroraFreshNeutral[300], 0.6),
      background: {
        default: '#FFFFFF',
        paper: '#FFFFFF',
      },
      text: {
        primary: auroraFreshNeutral[900],
        secondary: auroraFreshNeutral[600],
      },
      action: {
        hover: alpha(auroraFreshPrimary[600], 0.06),
        selected: alpha(auroraFreshPrimary[600], 0.1),
      },
      baseShadow: '0 4px 16px rgba(15, 23, 42, 0.1)',
    },
  },
  dark: {
    palette: {
      primary: {
        light: auroraFreshPrimary[300],
        main: auroraFreshPrimary[400],
        dark: auroraFreshPrimary[500],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: auroraFreshAccent[300],
        main: auroraFreshAccent[400],
        dark: auroraFreshAccent[500],
        contrastText: '#FFFFFF',
      },
      info: {
        light: auroraFreshAccent[200],
        main: auroraFreshAccent[400],
        dark: auroraFreshAccent[600],
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#FDE68A',
        main: '#FBBF24',
        dark: '#F59E0B',
        contrastText: '#000000',
      },
      error: {
        light: '#FCA5A5',
        main: '#F87171',
        dark: '#EF4444',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#6EE7B7',
        main: '#34D399',
        dark: '#10B981',
        contrastText: '#FFFFFF',
      },
      grey: {
        ...auroraFreshNeutral,
      },
      divider: alpha(auroraFreshNeutral[600], 0.5),
      background: {
        default: auroraFreshNeutral[900],
        paper: auroraFreshNeutral[800],
      },
      text: {
        primary: auroraFreshNeutral[50],
        secondary: auroraFreshNeutral[400],
      },
      action: {
        hover: alpha(auroraFreshPrimary[400], 0.12),
        selected: alpha(auroraFreshPrimary[400], 0.2),
      },
      baseShadow: '0 4px 16px rgba(15, 23, 42, 0.5)',
    },
  },
};

// ============================================================================
// NEO TOKYO - Cyberpunk, néons vibrants, futuriste
// ============================================================================
export const neoTokyoPrimary = {
  50: '#FFF1F2',
  100: '#FFE4E6',
  200: '#FECDD3',
  300: '#FDA4AF',
  400: '#FB7185',
  500: '#F43F5E',
  600: '#E11D48',
  700: '#BE123C',
  800: '#9F1239',
  900: '#881337',
};

export const neoTokyoNeon = {
  pink: '#FF0080',
  cyan: '#00D4FF',
  purple: '#8B5CF6',
  green: '#00FF88',
  yellow: '#FFD700',
};

export const neoTokyoNeutral = {
  50: '#F8FAFC',
  100: '#F0F4F8',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#3A3A4A',
  800: '#2A2A3A',
  900: '#16161F',
};

export const colorSchemesNeoTokyo = {
  light: {
    palette: {
      primary: {
        light: neoTokyoPrimary[400],
        main: neoTokyoPrimary[600],
        dark: neoTokyoPrimary[700],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: '#67E8F9',
        main: '#06B6D4',
        dark: '#0891B2',
        contrastText: '#FFFFFF',
      },
      info: {
        light: '#93C5FD',
        main: '#3B82F6',
        dark: '#1D4ED8',
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#FDE68A',
        main: '#F59E0B',
        dark: '#D97706',
        contrastText: '#000000',
      },
      error: {
        light: '#FECACA',
        main: '#EF4444',
        dark: '#B91C1C',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#A7F3D0',
        main: '#10B981',
        dark: '#047857',
        contrastText: '#FFFFFF',
      },
      grey: {
        ...neoTokyoNeutral,
      },
      divider: alpha(neoTokyoNeutral[300], 0.6),
      background: {
        default: '#F0F4F8',
        paper: '#FFFFFF',
      },
      text: {
        primary: neoTokyoNeutral[900],
        secondary: neoTokyoNeutral[600],
      },
      action: {
        hover: alpha(neoTokyoPrimary[600], 0.06),
        selected: alpha(neoTokyoPrimary[600], 0.1),
      },
      baseShadow: '0 0 20px rgba(225, 29, 72, 0.1)',
    },
  },
  dark: {
    palette: {
      primary: {
        light: '#FF4DA6',
        main: neoTokyoNeon.pink,
        dark: '#CC0066',
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: '#4DE4FF',
        main: neoTokyoNeon.cyan,
        dark: '#00A8CC',
        contrastText: '#000000',
      },
      info: {
        light: '#A78BFA',
        main: neoTokyoNeon.purple,
        dark: '#7C3AED',
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#FFE44D',
        main: neoTokyoNeon.yellow,
        dark: '#CCB000',
        contrastText: '#000000',
      },
      error: {
        light: '#FF6688',
        main: '#FF3366',
        dark: '#CC2952',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#4DFFAA',
        main: neoTokyoNeon.green,
        dark: '#00CC6A',
        contrastText: '#000000',
      },
      grey: {
        ...neoTokyoNeutral,
      },
      divider: alpha(neoTokyoNeutral[700], 0.6),
      background: {
        default: '#0A0A0F',
        paper: neoTokyoNeutral[900],
      },
      text: {
        primary: '#F8FAFC',
        secondary: neoTokyoNeutral[400],
      },
      action: {
        hover: alpha(neoTokyoNeon.pink, 0.12),
        selected: alpha(neoTokyoNeon.pink, 0.2),
      },
      baseShadow: '0 0 40px rgba(255, 0, 128, 0.2)',
    },
  },
};

// ============================================================================
// SAKURA - Zen japonais, rose doux, harmonie naturelle
// ============================================================================
export const sakuraPrimary = {
  50: '#FDF8F6',
  100: '#FBF3F0',
  200: '#F3E4DF',
  300: '#E8D5CF',
  400: '#E8A5AE',
  500: '#D4838F',
  600: '#B86B78',
  700: '#9A5666',
  800: '#7A4452',
  900: '#5A3340',
};

export const sakuraAccent = {
  50: '#F0F9F4',
  100: '#DBEEE3',
  200: '#B5CCC0',
  300: '#9BB8A6',
  400: '#7B9E89',
  500: '#5A8070',
  600: '#466958',
  700: '#355244',
  800: '#263D32',
  900: '#1A2922',
};

export const sakuraNeutral = {
  50: '#F5EDE8',
  100: '#EAD8D0',
  200: '#D5BEB2',
  300: '#C9B8B0',
  400: '#A89890',
  500: '#8B7872',
  600: '#6B5550',
  700: '#503D38',
  800: '#352D2B',
  900: '#1F1A19',
};

export const colorSchemesSakura = {
  light: {
    palette: {
      primary: {
        light: sakuraPrimary[400],
        main: sakuraPrimary[500],
        dark: sakuraPrimary[600],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: sakuraAccent[300],
        main: sakuraAccent[400],
        dark: sakuraAccent[500],
        contrastText: '#FFFFFF',
      },
      info: {
        light: '#BFDBFE',
        main: '#3B82F6',
        dark: '#1D4ED8',
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#E8C4A0',
        main: '#D4A574',
        dark: '#B8885A',
        contrastText: sakuraNeutral[900],
      },
      error: {
        light: '#E09090',
        main: '#C97070',
        dark: '#A85858',
        contrastText: '#FFFFFF',
      },
      success: {
        light: sakuraAccent[300],
        main: sakuraAccent[400],
        dark: sakuraAccent[500],
        contrastText: '#FFFFFF',
      },
      grey: {
        ...sakuraNeutral,
      },
      divider: alpha(sakuraPrimary[200], 0.6),
      background: {
        default: '#FDF8F6',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#3D2C29',
        secondary: sakuraNeutral[600],
      },
      action: {
        hover: alpha(sakuraPrimary[500], 0.08),
        selected: alpha(sakuraPrimary[500], 0.12),
      },
      baseShadow: '0 4px 12px rgba(61, 44, 41, 0.06)',
    },
  },
  dark: {
    palette: {
      primary: {
        light: sakuraPrimary[300],
        main: sakuraPrimary[400],
        dark: sakuraPrimary[500],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: sakuraAccent[200],
        main: sakuraAccent[300],
        dark: sakuraAccent[400],
        contrastText: '#FFFFFF',
      },
      info: {
        light: '#93C5FD',
        main: '#60A5FA',
        dark: '#3B82F6',
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#E8C4A0',
        main: '#D4A574',
        dark: '#B8885A',
        contrastText: sakuraNeutral[900],
      },
      error: {
        light: '#E09090',
        main: '#D46060',
        dark: '#B84848',
        contrastText: '#FFFFFF',
      },
      success: {
        light: sakuraAccent[200],
        main: sakuraAccent[300],
        dark: sakuraAccent[400],
        contrastText: '#FFFFFF',
      },
      grey: {
        ...sakuraNeutral,
      },
      divider: alpha(sakuraNeutral[700], 0.5),
      background: {
        default: sakuraNeutral[900],
        paper: sakuraNeutral[800],
      },
      text: {
        primary: sakuraNeutral[50],
        secondary: sakuraNeutral[300],
      },
      action: {
        hover: alpha(sakuraPrimary[400], 0.15),
        selected: alpha(sakuraPrimary[400], 0.25),
      },
      baseShadow: '0 4px 12px rgba(31, 26, 25, 0.5)',
    },
  },
};

// ============================================================================
// MONOCHROME PRO - Professionnel, sobre, efficace
// ============================================================================
export const monochromeProNeutral = {
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#E5E5E5',
  300: '#D4D4D4',
  400: '#A3A3A3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
};

export const monochromeProAccent = {
  50: '#EFF6FF',
  100: '#DBEAFE',
  200: '#BFDBFE',
  300: '#93C5FD',
  400: '#60A5FA',
  500: '#3B82F6',
  600: '#2563EB',
  700: '#1D4ED8',
  800: '#1E40AF',
  900: '#1E3A8A',
};

export const colorSchemesMonochromePro = {
  light: {
    palette: {
      primary: {
        light: monochromeProNeutral[600],
        main: monochromeProNeutral[800],
        dark: monochromeProNeutral[900],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: monochromeProAccent[400],
        main: monochromeProAccent[600],
        dark: monochromeProAccent[700],
        contrastText: '#FFFFFF',
      },
      info: {
        light: monochromeProAccent[300],
        main: monochromeProAccent[500],
        dark: monochromeProAccent[700],
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#FDE68A',
        main: '#D97706',
        dark: '#B45309',
        contrastText: '#FFFFFF',
      },
      error: {
        light: '#FECACA',
        main: '#DC2626',
        dark: '#B91C1C',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#A7F3D0',
        main: '#059669',
        dark: '#047857',
        contrastText: '#FFFFFF',
      },
      grey: {
        ...monochromeProNeutral,
      },
      divider: alpha(monochromeProNeutral[300], 0.6),
      background: {
        default: monochromeProNeutral[50],
        paper: '#FFFFFF',
      },
      text: {
        primary: monochromeProNeutral[900],
        secondary: monochromeProNeutral[600],
      },
      action: {
        hover: alpha(monochromeProNeutral[800], 0.04),
        selected: alpha(monochromeProNeutral[800], 0.08),
      },
      baseShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: {
        light: monochromeProNeutral[200],
        main: monochromeProNeutral[50],
        dark: monochromeProNeutral[100],
        contrastText: monochromeProNeutral[900],
      },
      secondary: {
        light: monochromeProAccent[300],
        main: monochromeProAccent[400],
        dark: monochromeProAccent[500],
        contrastText: '#FFFFFF',
      },
      info: {
        light: monochromeProAccent[300],
        main: monochromeProAccent[400],
        dark: monochromeProAccent[600],
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#FDE68A',
        main: '#F59E0B',
        dark: '#D97706',
        contrastText: '#000000',
      },
      error: {
        light: '#FCA5A5',
        main: '#EF4444',
        dark: '#DC2626',
        contrastText: '#FFFFFF',
      },
      success: {
        light: '#6EE7B7',
        main: '#10B981',
        dark: '#059669',
        contrastText: '#FFFFFF',
      },
      grey: {
        ...monochromeProNeutral,
      },
      divider: alpha(monochromeProNeutral[700], 0.5),
      background: {
        default: '#0A0A0A',
        paper: monochromeProNeutral[900],
      },
      text: {
        primary: monochromeProNeutral[50],
        secondary: monochromeProNeutral[400],
      },
      action: {
        hover: alpha(monochromeProNeutral[50], 0.08),
        selected: alpha(monochromeProNeutral[50], 0.12),
      },
      baseShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
    },
  },
};

// ============================================================================
// TERRACOTTA - Méditerranéen, terreux, chaleureux
// ============================================================================
export const terracottaPrimary = {
  50: '#FEF3EE',
  100: '#FCE4D6',
  200: '#F9C8AD',
  300: '#F4A17A',
  400: '#E88A5E',
  500: '#D47A58',
  600: '#C45D35',
  700: '#A34A28',
  800: '#823B20',
  900: '#612D18',
};

export const terracottaAccent = {
  50: '#EFFBF7',
  100: '#D1F2E6',
  200: '#A3E5CD',
  300: '#7AB8A0',
  400: '#5A9A82',
  500: '#3D7A65',
  600: '#2D5A4A',
  700: '#1F4035',
  800: '#152A24',
  900: '#0C1815',
};

export const terracottaNeutral = {
  50: '#FAF6F1',
  100: '#F5EDE3',
  200: '#E8DDD0',
  300: '#D4C4B0',
  400: '#B8A590',
  500: '#8B7666',
  600: '#6B5547',
  700: '#503E32',
  800: '#352D29',
  900: '#1A1513',
};

export const colorSchemesTerracotta = {
  light: {
    palette: {
      primary: {
        light: terracottaPrimary[400],
        main: terracottaPrimary[600],
        dark: terracottaPrimary[700],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: terracottaAccent[400],
        main: terracottaAccent[600],
        dark: terracottaAccent[700],
        contrastText: '#FFFFFF',
      },
      info: {
        light: '#BFDBFE',
        main: '#3B82F6',
        dark: '#1D4ED8',
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#E8C070',
        main: '#D4A24E',
        dark: '#B8883A',
        contrastText: terracottaNeutral[900],
      },
      error: {
        light: '#D46060',
        main: '#B84444',
        dark: '#983636',
        contrastText: '#FFFFFF',
      },
      success: {
        light: terracottaAccent[400],
        main: terracottaAccent[600],
        dark: terracottaAccent[700],
        contrastText: '#FFFFFF',
      },
      grey: {
        ...terracottaNeutral,
      },
      divider: alpha(terracottaNeutral[300], 0.6),
      background: {
        default: terracottaNeutral[50],
        paper: '#FFFFFF',
      },
      text: {
        primary: '#3D2E24',
        secondary: terracottaNeutral[600],
      },
      action: {
        hover: alpha(terracottaPrimary[600], 0.06),
        selected: alpha(terracottaPrimary[600], 0.1),
      },
      baseShadow: '0 4px 12px rgba(61, 46, 36, 0.08)',
    },
  },
  dark: {
    palette: {
      primary: {
        light: terracottaPrimary[300],
        main: terracottaPrimary[400],
        dark: terracottaPrimary[500],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: terracottaAccent[300],
        main: terracottaAccent[400],
        dark: terracottaAccent[500],
        contrastText: '#FFFFFF',
      },
      info: {
        light: '#93C5FD',
        main: '#60A5FA',
        dark: '#3B82F6',
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#E8C070',
        main: '#D4A24E',
        dark: '#B8883A',
        contrastText: terracottaNeutral[900],
      },
      error: {
        light: '#D46060',
        main: '#B84444',
        dark: '#983636',
        contrastText: '#FFFFFF',
      },
      success: {
        light: terracottaAccent[300],
        main: terracottaAccent[400],
        dark: terracottaAccent[500],
        contrastText: '#FFFFFF',
      },
      grey: {
        ...terracottaNeutral,
      },
      divider: alpha(terracottaNeutral[700], 0.5),
      background: {
        default: terracottaNeutral[900],
        paper: terracottaNeutral[800],
      },
      text: {
        primary: terracottaNeutral[100],
        secondary: terracottaNeutral[300],
      },
      action: {
        hover: alpha(terracottaPrimary[400], 0.15),
        selected: alpha(terracottaPrimary[400], 0.25),
      },
      baseShadow: '0 4px 12px rgba(26, 21, 19, 0.5)',
    },
  },
};

// ============================================================================
// NORDIC FROST - Scandinave, minimaliste, frais
// ============================================================================
export const nordicFrostPrimary = {
  50: '#EFF6FF',
  100: '#DBEAFE',
  200: '#BFDBFE',
  300: '#93C5FD',
  400: '#60A5FA',
  500: '#3B82F6',
  600: '#2563EB',
  700: '#1D4ED8',
  800: '#1E40AF',
  900: '#1E3A8A',
};

export const nordicFrostAccent = {
  50: '#ECFDF5',
  100: '#D1FAE5',
  200: '#A7F3D0',
  300: '#6EE7B7',
  400: '#34D399',
  500: '#10B981',
  600: '#059669',
  700: '#047857',
  800: '#065F46',
  900: '#064E3B',
};

export const nordicFrostNeutral = {
  50: '#F8FAFC',
  100: '#F1F5F9',
  200: '#E2E8F0',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1E293B',
  900: '#0F172A',
};

export const colorSchemesNordicFrost = {
  light: {
    palette: {
      primary: {
        light: nordicFrostPrimary[400],
        main: '#0F4C81',
        dark: '#0D3F6A',
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: nordicFrostAccent[400],
        main: nordicFrostAccent[600],
        dark: nordicFrostAccent[700],
        contrastText: '#FFFFFF',
      },
      info: {
        light: nordicFrostPrimary[300],
        main: nordicFrostPrimary[500],
        dark: nordicFrostPrimary[700],
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#FDE68A',
        main: '#D97706',
        dark: '#B45309',
        contrastText: '#FFFFFF',
      },
      error: {
        light: '#FECACA',
        main: '#DC2626',
        dark: '#B91C1C',
        contrastText: '#FFFFFF',
      },
      success: {
        light: nordicFrostAccent[300],
        main: nordicFrostAccent[600],
        dark: nordicFrostAccent[700],
        contrastText: '#FFFFFF',
      },
      grey: {
        ...nordicFrostNeutral,
      },
      divider: alpha(nordicFrostNeutral[300], 0.6),
      background: {
        default: '#F7F9FB',
        paper: '#FFFFFF',
      },
      text: {
        primary: nordicFrostNeutral[800],
        secondary: nordicFrostNeutral[600],
      },
      action: {
        hover: alpha(nordicFrostPrimary[600], 0.06),
        selected: alpha(nordicFrostPrimary[600], 0.1),
      },
      baseShadow: '0 4px 12px rgba(15, 23, 42, 0.06)',
    },
  },
  dark: {
    palette: {
      primary: {
        light: nordicFrostPrimary[300],
        main: nordicFrostPrimary[400],
        dark: nordicFrostPrimary[500],
        contrastText: '#FFFFFF',
      },
      secondary: {
        light: nordicFrostAccent[300],
        main: nordicFrostAccent[400],
        dark: nordicFrostAccent[500],
        contrastText: '#FFFFFF',
      },
      info: {
        light: nordicFrostPrimary[300],
        main: nordicFrostPrimary[400],
        dark: nordicFrostPrimary[600],
        contrastText: '#FFFFFF',
      },
      warning: {
        light: '#FDE68A',
        main: '#FBBF24',
        dark: '#F59E0B',
        contrastText: '#000000',
      },
      error: {
        light: '#FCA5A5',
        main: '#F87171',
        dark: '#EF4444',
        contrastText: '#FFFFFF',
      },
      success: {
        light: nordicFrostAccent[300],
        main: nordicFrostAccent[400],
        dark: nordicFrostAccent[500],
        contrastText: '#FFFFFF',
      },
      grey: {
        ...nordicFrostNeutral,
      },
      divider: alpha(nordicFrostNeutral[600], 0.5),
      background: {
        default: '#0F1419',
        paper: nordicFrostNeutral[800],
      },
      text: {
        primary: nordicFrostNeutral[100],
        secondary: nordicFrostNeutral[400],
      },
      action: {
        hover: alpha(nordicFrostPrimary[400], 0.12),
        selected: alpha(nordicFrostPrimary[400], 0.2),
      },
      baseShadow: '0 4px 12px rgba(15, 23, 42, 0.5)',
    },
  },
};
